import { ampli } from '@/ampli';
import Link from 'next/link';

import { ByZeroExPill } from '@/components/Landing/Utils/ByZeroExPill';

import { fontStyles } from '@/styles/typography';

import { ExternalLink } from '@/ui/Link/ExternalLink';
import { Logo } from '@/ui/Logo/Logo';
import { LogoCup } from '@/ui/Logo/LogoCup';

// utils
import { classNames } from '@/utils/classnames';
import { LS_THRESHOLDS } from '@/utils/liquidityScore';
import { routes } from '@/utils/routes';

interface FooterProps {
  isHomepage?: boolean;
}

const linkClass = classNames(
  'text-token-gray-800',
  'inline-flex',
  'items-center',
  'hover:text-token-gray-1600',
  'focus:text-token-gray-1600',
  'focus:outline-token-gray-500',
  'disabled:text-token-gray-900',
  'disabled:cursor-not-allowed',
  'group',
);

export function Footer({ isHomepage }: FooterProps) {
  return (
    <footer
      className={classNames(
        'border-solid border-t-token-gray-300 border-t pt-12 px-6',
        isHomepage ? 'pb-16' : 'pb-10',
      )}
    >
      <div className="flex flex-wrap desktop:flex-nowrap justify-between mx-auto max-w-[1240px] w-full gap-0 desktop:gap-5">
        <div className="mb-10 w-full ml-4">
          <div className="text-token-gray-700 flex items-center">
            <Link href={{ pathname: routes.INDEX }}>
              <div className="flex items-center mr-3 hover:text-token-gray-800">
                <LogoCup className="mr-2.5" />
                <Logo height={16} />
              </div>
            </Link>
            <ByZeroExPill className="inline-block mt-1" />
          </div>
        </div>
        <div className="shrink-0 mb-5 w-1/2 tablet:w-1/4 desktop:w-[200px]">
          <h5 className={fontStyles({ variant: 'H5' })}>Learn</h5>
          <ul
            className={fontStyles({
              variant: 'BODY_MD',
              class: ['flex', 'flex-col', 'flex-wrap', 'desktop:flex-nowrap', 'mt-4', 'gap-y-2'],
            })}
          >
            <li>
              <ExternalLink
                className={linkClass}
                href={routes.SWAP_TOKENS}
                onClick={() => ampli.footerSwapClicked()}
              >
                <span>Swap tokens</span>
              </ExternalLink>
            </li>
            <li>
              <ExternalLink
                className={linkClass}
                href={routes.LIMIT_ORDERS}
                onClick={() => ampli.footerLimitClicked()}
              >
                <span>Limit orders</span>
              </ExternalLink>
            </li>
            <li>
              <ExternalLink
                className={linkClass}
                href={routes.CROSS_CHAIN_SWAPS}
                onClick={() => ampli.footerCrossChainClicked()}
              >
                <span>Cross chain</span>
              </ExternalLink>
            </li>
            <li>
              <ExternalLink className={linkClass} href={routes.BLOG}>
                <span>Blog</span>
              </ExternalLink>
            </li>
            <li>
              <ExternalLink className={linkClass} href={routes.ABOUT_MATCHA}>
                <span>Company</span>
              </ExternalLink>
            </li>
            <li>
              <ExternalLink className={linkClass} href={routes.ZEROEX_HOME}>
                <span>Developer APIs</span>
              </ExternalLink>
            </li>
          </ul>
        </div>
        <div className="shrink-0 mb-5 w-1/2 tablet:w-1/4 desktop:w-[200px]">
          <h5 className={fontStyles({ variant: 'H5' })}>Support</h5>
          <ul
            className={fontStyles({
              variant: 'BODY_MD',
              class: ['flex', 'flex-col', 'flex-wrap', 'desktop:flex-nowrap', 'mt-4', 'gap-y-2'],
            })}
          >
            <li>
              <ExternalLink className={linkClass} href={routes.HELP}>
                <span>Help center</span>
              </ExternalLink>
            </li>
            <li>
              <ExternalLink className={linkClass} href={routes.HELP_FAQ}>
                <span>FAQs</span>
              </ExternalLink>
            </li>
            <li>
              <ExternalLink className={linkClass} href={routes.HELP_GETTING_STARTED}>
                <span>Getting started</span>
              </ExternalLink>
            </li>
            <li>
              <ExternalLink
                className={linkClass}
                href={routes.BRAND_ASSETS}
                aria-label="Download brand assets"
              >
                <span>Brand assets</span>
              </ExternalLink>
            </li>
          </ul>
        </div>
        <div className="shrink-0 mb-5 w-1/2 tablet:w-1/4 desktop:w-[200px]">
          <h5 className={fontStyles({ variant: 'H5' })}>Legal</h5>
          <ul
            className={fontStyles({
              variant: 'BODY_MD',
              class: ['flex', 'flex-col', 'flex-wrap', 'desktop:flex-nowrap', 'mt-4', 'gap-y-2'],
            })}
          >
            <li>
              <Link className={linkClass} href={{ pathname: routes.TERMS }}>
                <span>Terms</span>
              </Link>
            </li>
            <li>
              <Link className={linkClass} href={{ pathname: routes.PRIVACY }}>
                <span>Privacy</span>
              </Link>
            </li>
          </ul>
        </div>
        <div className="shrink-0 mb-5 w-1/2 tablet:w-1/4 desktop:w-[200px]">
          <h5 className={fontStyles({ variant: 'H5' })}>Community</h5>
          <ul
            className={fontStyles({
              variant: 'BODY_MD',
              class: ['flex', 'flex-col', 'flex-wrap', 'desktop:flex-nowrap', 'mt-4', 'gap-y-2'],
            })}
          >
            <li>
              <ExternalLink className={linkClass} href={routes.TWITTER}>
                <span>X.com</span>
              </ExternalLink>
            </li>
            <li>
              <ExternalLink className={linkClass} href={routes.DISCORD}>
                <span>Discord</span>
              </ExternalLink>
            </li>
          </ul>
        </div>
      </div>
      {isHomepage && (
        <section
          title="disclaimer"
          className={fontStyles({
            variant: 'BODY_SM',
            class: [
              'mt-5',
              'desktop:mt-9',
              'text-token-gray-700',
              'mx-auto',
              'max-w-[1240px]',
              'desktop:pl-4',
              'flex',
              'flex-col',
              'gap-y-2',
            ],
          })}
        >
          <span>
            <span className="text-token-gray-800">Trending</span> tokens are ranked by{' '}
            <ExternalLink className="underline" href={routes.CODEX_IO}>
              codex.io
            </ExternalLink>
          </span>
          <span>
            <span className="text-token-gray-800">Liquidity Score</span> displayed as &quot;
            <span className="text-token-gray-800">*Liquidity</span>&quot; represents a measure of
            the liquidity available within {LS_THRESHOLDS.ZORA_POST}% of the spot price for zora
            coins, and {LS_THRESHOLDS.DEFAULT}% of the spot price for all other tokens.
          </span>
          <span>
            <span className="text-token-gray-800">Security Audit</span> displayed as &quot;
            <span className="text-token-gray-800">*Security</span>&quot; identifies risks present in
            the token’s code. Audit results are generated by Go+ Security and are not verified by
            Matcha. Matcha does not guarantee accuracy.
            <br />
            Do your own research before making any purchase decisions.
          </span>
        </section>
      )}
    </footer>
  );
}
